import * as React from "react";

import { useTranslation } from "react-i18next";

import Button from "@/components/button";
import Input from "@/components/input";
import { MonthYearPicker } from "@/components/month-year-picker";
import {
  ROLE_GROUPS,
  emptyWorkExperience,
  isWorkExperienceComplete,
  levelForDuration,
} from "@/constants/onboarding";
import type { WorkExperience } from "@/constants/onboarding";
import {
  useOnboardingDraft,
  useSaveOnboardingExperience,
} from "@/queries/api/onboardingApi";
import type { OnboardingProfileResponse } from "@/schemas/onboarding";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";

function groupRoleExperiences(
  source: OnboardingProfileResponse["roleInterests"],
): Array<WorkExperience> {
  const groups = new Map<string, WorkExperience>();
  for (const r of source) {
    if (!r.restaurant || !r.startMonth) continue;
    const key = `${r.restaurant}|${r.startMonth}|${r.endMonth ?? ""}|${r.current ? "1" : "0"}`;
    const existing = groups.get(key);
    if (existing) {
      if (!existing.roles.includes(r.roleKey)) existing.roles.push(r.roleKey);
    } else {
      groups.set(key, {
        restaurant: r.restaurant,
        start: r.startMonth,
        end: r.endMonth ?? "",
        current: r.current,
        roles: [r.roleKey],
      });
    }
  }
  return Array.from(groups.values());
}

export function WorkExperiencePanel() {
  const { t } = useTranslation("settings");
  const draftQuery = useOnboardingDraft();
  const saveExperienceMutation = useSaveOnboardingExperience();

  const [workExperiences, setWorkExperiences] = React.useState<
    Array<WorkExperience>
  >([emptyWorkExperience()]);
  const [noExperience, setNoExperience] = React.useState(false);
  const [hydrated, setHydrated] = React.useState(false);

  // Hydrate once from the onboarding draft (one card per (restaurant, range)).
  // Prefer the new roleExperiences key; fall back to the legacy roleInterests key.
  React.useEffect(() => {
    const draft = draftQuery.data;
    if (!draft || hydrated) return;
    const source = draft.roleExperiences ?? draft.roleInterests;
    const grouped = groupRoleExperiences(source);
    if (grouped.length > 0) {
      setWorkExperiences(grouped);
      setNoExperience(false);
    } else if (source.some((r) => r.level === "none")) {
      // BE records "noExperience" as a single level=none row with no restaurant.
      setWorkExperiences([]);
      setNoExperience(true);
    }
    setHydrated(true);
  }, [draftQuery.data, hydrated]);

  function addExperience() {
    setWorkExperiences((prev) => [...prev, emptyWorkExperience()]);
  }

  function removeExperience(index: number) {
    setWorkExperiences((prev) => prev.filter((_, i) => i !== index));
  }

  function setField(
    index: number,
    field: keyof Omit<WorkExperience, "roles">,
    value: string | boolean,
  ) {
    setWorkExperiences((prev) =>
      prev.map((we, i) => {
        if (i !== index) return we;
        const updated: WorkExperience = { ...we, [field]: value };
        if (field === "current" && value === true) updated.end = "";
        return updated;
      }),
    );
  }

  function toggleRole(index: number, roleKey: string) {
    setWorkExperiences((prev) =>
      prev.map((we, i) => {
        if (i !== index) return we;
        const has = we.roles.includes(roleKey);
        return {
          ...we,
          roles: has
            ? we.roles.filter((r) => r !== roleKey)
            : [...we.roles, roleKey],
        };
      }),
    );
  }

  function chooseNoExperience(value: boolean) {
    setNoExperience(value);
    setWorkExperiences(value ? [] : [emptyWorkExperience()]);
  }

  async function onSave() {
    if (!noExperience) {
      if (workExperiences.length === 0) {
        notify.error(t("workExperience.addAtLeastOne"));
        return;
      }
      const incomplete = workExperiences.findIndex(
        (we) => !isWorkExperienceComplete(we),
      );
      if (incomplete !== -1) {
        notify.error(t("workExperience.incomplete"));
        return;
      }
    }

    // "New to Restaurants" sends an empty array; otherwise flatten per-role rows.
    const roleExperiences = noExperience
      ? []
      : workExperiences.flatMap((we) => {
          const restaurant = we.restaurant.trim();
          if (!restaurant || !we.start || we.roles.length === 0) return [];
          return we.roles.map((roleKey) => ({
            roleKey,
            level: levelForDuration(we.start, we.end, we.current),
            restaurant,
            startMonth: we.start,
            endMonth: we.current ? undefined : we.end || undefined,
            current: we.current,
          }));
        });

    try {
      await saveExperienceMutation.mutateAsync({ roleExperiences });
      notify.success(t("workExperience.saved"));
    } catch (err) {
      Logger.error("settings work experience save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
      notify.error(
        err instanceof Error ? err.message : t("workExperience.saveError"),
      );
    }
  }

  if (draftQuery.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <h3 className="text-xl font-semibold text-garnet mb-2">
          {t("workExperience.title")}
        </h3>
        <p className="body-small text-garnet">
          {t("actions.loading", { ns: "common" })}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-xl font-semibold text-garnet mb-1">
          {t("workExperience.title")}
        </h3>
        <p className="body-small text-gray-600">
          {t("workExperience.subtitle")}
        </p>
      </div>

      {noExperience ? (
        <div className="rounded-lg ring-1 ring-garnet/20 p-4 flex flex-col gap-3">
          <h4 className="font-semibold text-gray-700">
            {t("workExperience.newToRestaurantsTitle")}
          </h4>
          <p className="body-small text-gray-600">
            {t("workExperience.newToRestaurantsDescription")}
          </p>
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => chooseNoExperience(false)}
            >
              {t("workExperience.workedBefore")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => chooseNoExperience(true)}
              className="w-full md:w-auto"
            >
              {t("workExperience.newToRestaurants")}
            </Button>
          </div>
          {workExperiences.map((we, index) => (
            <div
              key={index}
              className="rounded-lg ring-1 ring-garnet/20 p-4 flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-gray-700">
                  {t("workExperience.restaurantIndex", { index: index + 1 })}
                </h4>
                {workExperiences.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeExperience(index)}
                    className="text-sm text-garnet underline hover:opacity-80"
                  >
                    {t("workExperience.remove")}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Input
                    label={t("workExperience.restaurantName")}
                    placeholder={t("workExperience.restaurantPlaceholder")}
                    value={we.restaurant}
                    onChange={(e) =>
                      setField(index, "restaurant", e.currentTarget.value)
                    }
                  />
                </div>
                <MonthYearPicker
                  label={t("workExperience.startMonth")}
                  value={we.start}
                  onChange={(v) => setField(index, "start", v ?? "")}
                />
                <MonthYearPicker
                  label={t("workExperience.endMonth")}
                  value={we.end}
                  onChange={(v) => setField(index, "end", v ?? "")}
                  disabled={we.current}
                />
                <label className="flex items-center gap-2 mt-2 md:col-span-2">
                  <input
                    type="checkbox"
                    checked={we.current}
                    onChange={(e) =>
                      setField(index, "current", e.currentTarget.checked)
                    }
                  />
                  <span className="body-small">
                    {t("workExperience.currentlyWorkHere")}
                  </span>
                </label>
              </div>
              <div className="flex flex-col gap-3">
                <h5 className="font-semibold text-gray-700">
                  {t("workExperience.rolesHeld")}
                </h5>
                {ROLE_GROUPS.map((g) => (
                  <div key={g.group} className="flex flex-col gap-2">
                    <span className="body-small text-gray-500">{g.group}</span>
                    <div className="flex flex-wrap gap-2">
                      {g.roles.map(([key, label, icon]) => {
                        const selected = we.roles.includes(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            role="checkbox"
                            aria-pressed={selected}
                            aria-checked={selected}
                            onClick={() => toggleRole(index, key)}
                            className={
                              "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors " +
                              (selected
                                ? "bg-garnet text-white ring-1 ring-garnet"
                                : "bg-white text-garnet ring-1 ring-garnet/30 hover:ring-garnet")
                            }
                          >
                            <span aria-hidden>{icon}</span>
                            <span>{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={addExperience}
              className="w-full md:w-auto"
            >
              {t("workExperience.addAnotherRestaurant")}
            </Button>
          </div>
        </>
      )}

      <div className="flex justify-end mt-2">
        <Button
          type="button"
          variant="primary"
          onClick={onSave}
          disabled={saveExperienceMutation.isPending}
        >
          {saveExperienceMutation.isPending
            ? t("actions.saving", { ns: "common" })
            : t("actions.saveChanges", { ns: "common" })}
        </Button>
      </div>
    </div>
  );
}
